document.addEventListener('alpine:init', () => {
    Alpine.store('cart', {
        data: Alpine.$persist({}).as('cartData'),
        cartSectionId: '',
        refreshSections: '',
        _opCount: 0,
        _dataSnapshot: null,
        _postProcessors: [],
        subscriptionMaxQty: 3,
        quarterlyMaxQty: 1,
        hasActiveQuarterlySub: false,

        /**
         * Initializes the cart store.
         * @returns {void}
         */
        init() {
            console.log('Cart store initialized');
            this.onCartAdd = this.cartAddEvent.bind(this);
            this.hydrateFromJson();
            this.initSectionsToRefresh();
            this.addEventListeners();
            this.initDirectToCheckoutLabel();
            this.refreshSkioQuarterlyStatus();
        },

        /**
         * True when a cart line is a paid subscription (not OTP / free gift).
         * @param {Object} item
         * @returns {boolean}
         */
        isSubscriptionLine(item) {
            if (!item?.selling_plan_allocation) return false;
            if (item?.properties?._isFreeGift === 'true') return false;
            return true;
        },

        /**
         * True when a cart line is a quarterly / 90-day subscription.
         * @param {Object} item
         * @returns {boolean}
         */
        isQuarterlyLine(item) {
            if (!this.isSubscriptionLine(item)) return false;
            const handle = String(item.handle || '').toLowerCase();
            const plan = item.selling_plan_allocation?.selling_plan;
            const interval = Number(plan?.interval_count) || 0;
            const planName = String(plan?.name || '').toLowerCase();
            return (
                handle.includes('quarterly') ||
                interval >= 3 ||
                item.properties?.upgraded_in_cart_quarterly === 'true' ||
                /90/.test(planName) ||
                planName.includes('quarter')
            );
        },

        /**
         * Sum of quarterly subscription units in the cart.
         * @param {{ excludeKey?: string, excludeKeys?: string[] }} [options]
         * @returns {number}
         */
        getQuarterlyQty({ excludeKey, excludeKeys } = {}) {
            const skip = new Set(
                (Array.isArray(excludeKeys) ? excludeKeys : [])
                    .concat(excludeKey ? [excludeKey] : [])
                    .map(String)
            );
            return this.items.reduce((sum, item) => {
                if (!this.isQuarterlyLine(item)) return sum;
                if (skip.has(String(item.key))) return sum;
                return sum + (Number(item.quantity) || 0);
            }, 0);
        },

        /**
         * How many more quarterly units can be added.
         * @param {{ excludeKey?: string, excludeKeys?: string[] }} [options]
         * @returns {number}
         */
        getQuarterlyRemainingQty({ excludeKey, excludeKeys } = {}) {
            const cartRemaining = Math.max(
                0,
                this.quarterlyMaxQty - this.getQuarterlyQty({ excludeKey, excludeKeys })
            );
            if (this.hasActiveQuarterlySub) return 0;
            return cartRemaining;
        },

        /**
         * Best-effort Skio storefront check for an active quarterly subscription.
         * Fails open (leaves hasActiveQuarterlySub false) if the API is unavailable.
         * @returns {Promise<void>}
         */
        async refreshSkioQuarterlyStatus() {
            try {
                let userId = '';
                try {
                    userId = window.localStorage.getItem('storefrontUserId') || '';
                } catch (_storageError) {
                    return;
                }
                if (!userId) return;

                const domain = window.Shopify?.shop || window.location.hostname;
                const url =
                    `https://api.skio.com/storefront-http/get-subscriptions-by-storefront-user-id` +
                    `?storefrontUserId=${encodeURIComponent(userId)}` +
                    `&domain=${encodeURIComponent(domain)}`;
                const response = await fetch(url, { credentials: 'include' });
                if (!response.ok) return;
                const data = await response.json();
                const list = Array.isArray(data)
                    ? data
                    : data?.subscriptions || data?.data || data?.items || [];
                this.hasActiveQuarterlySub = list.some(
                    (sub) => this.isSkioSubscriptionActive(sub) && this.isSkioSubscriptionQuarterly(sub)
                );
            } catch (_error) {
                /* fail open — cart cap still applies */
            }
        },

        /**
         * @param {Object} sub
         * @returns {boolean}
         */
        isSkioSubscriptionActive(sub) {
            const status = String(sub?.status || sub?.state || sub?.subscriptionStatus || '').toLowerCase();
            if (!status) return true;
            return ['active', 'trialing', 'paused', 'pause'].includes(status);
        },

        /**
         * @param {Object} sub
         * @returns {boolean}
         */
        isSkioSubscriptionQuarterly(sub) {
            const handle = String(
                sub?.productHandle || sub?.product?.handle || sub?.handle || ''
            ).toLowerCase();
            const interval = Number(
                sub?.billingPolicy?.intervalCount ||
                    sub?.intervalCount ||
                    sub?.deliveryPolicy?.intervalCount ||
                    0
            );
            const name = String(
                sub?.sellingPlanName || sub?.planName || sub?.name || ''
            ).toLowerCase();
            return (
                handle.includes('quarterly') ||
                interval >= 3 ||
                /90/.test(name) ||
                name.includes('quarter')
            );
        },

        /**
         * Sum of subscription quantities for a product across all variants/plans.
         * @param {number|string} productId
         * @param {{ excludeKey?: string, excludeKeys?: string[] }} [options]
         * @returns {number}
         */
        getSubscriptionProductQty(productId, { excludeKey, excludeKeys } = {}) {
            const id = Number(productId);
            if (!Number.isFinite(id) || id <= 0) return 0;

            const skip = new Set(
                (Array.isArray(excludeKeys) ? excludeKeys : [])
                    .concat(excludeKey ? [excludeKey] : [])
                    .map(String)
            );

            return this.items.reduce((sum, item) => {
                if (!this.isSubscriptionLine(item)) return sum;
                if (Number(item.product_id) !== id) return sum;
                if (skip.has(String(item.key))) return sum;
                return sum + (Number(item.quantity) || 0);
            }, 0);
        },

        /**
         * How many more subscription units can be added for a product.
         * @param {number|string} productId
         * @param {{ excludeKey?: string }} [options]
         * @returns {number}
         */
        getSubscriptionRemainingQty(productId, { excludeKey, quarterly = false } = {}) {
            if (quarterly) return this.getQuarterlyRemainingQty({ excludeKey });
            return Math.max(
                0,
                this.subscriptionMaxQty - this.getSubscriptionProductQty(productId, { excludeKey })
            );
        },

        /**
         * Max quantity allowed on a subscription cart line given other lines
         * of the same product already in the cart.
         * @param {Object} item
         * @returns {number}
         */
        getSubscriptionMaxQtyForLine(item) {
            if (!this.isSubscriptionLine(item)) return Number.POSITIVE_INFINITY;
            // A grouped display row stands in for several real lines, so every
            // key it covers has to be excluded from the "others" total.
            const excludeKeys = Array.isArray(item._groupKeys) && item._groupKeys.length
                ? item._groupKeys
                : [item.key];
            if (this.isQuarterlyLine(item)) {
                return this.getQuarterlyRemainingQty({ excludeKeys });
            }
            const others = this.getSubscriptionProductQty(item.product_id, { excludeKeys });
            return Math.max(0, this.subscriptionMaxQty - others);
        },

        /**
         * Begins a cart operation. Reference-counted: the first call
         * captures a snapshot of `data` that the display* getters return
         * until the matching endOp() brings the counter back to zero.
         * @returns {void}
         */
        beginOp() {
            if (this._opCount === 0) {
                this._dataSnapshot = this.data ? JSON.parse(JSON.stringify(this.data)) : {};
            }
            this._opCount++;
        },

        /**
         * Ends a cart operation. When the counter reaches zero the snapshot
         * is cleared and the display* getters fall back to live `data`.
         * @returns {void}
         */
        endOp() {
            this._opCount = Math.max(0, this._opCount - 1);
            if (this._opCount === 0) {
                this._dataSnapshot = null;
            }
        },

        /**
         * Registers a post-processor that runs (and is awaited) after every
         * cart-mutating fetch but before the display snapshot is released.
         * Used by cartGwp to reconcile managed gift lines.
         * @param {Function} fn - Async or sync function.
         * @returns {void}
         */
        registerPostProcessor(fn) {
            if (typeof fn !== 'function') return;
            this._postProcessors.push(fn);
        },

        /**
         * Runs registered post-processors sequentially. Errors in one
         * processor do not block subsequent ones.
         * @param {Object} [context] - Passed to each processor (e.g. skipSections).
         * @returns {Promise<void>}
         */
        async runPostProcessors(context = {}) {
            for (const fn of this._postProcessors) {
                try {
                    await fn(context);
                } catch (error) {
                    console.error('[cart] post-processor failed', error);
                }
            }
        },

        /**
         * Resolves when `promise` settles or `ms` elapses, whichever is first.
         * Used so a hung GWP fetch cannot block the DTC redirect. The original
         * promise is not cancelled; its rejection is swallowed after timeout.
         * @param {Promise} promise
         * @param {number} ms
         * @returns {Promise<void>}
         */
        async withTimeout(promise, ms) {
            let timer;
            try {
                await Promise.race([
                    Promise.resolve(promise).catch((error) => {
                        console.error('[cart] timed operation failed', error);
                    }),
                    new Promise((resolve) => {
                        timer = setTimeout(resolve, ms);
                    }),
                ]);
            } finally {
                clearTimeout(timer);
            }
        },

        /**
         * Redirects to checkout. Optional discount code wraps the destination
         * as /discount/<code>?redirect=<checkout>.
         * @param {string} [discountCode]
         * @returns {void}
         */
        goToCheckout(discountCode) {
            let url = this.checkoutUrl();
            const code = String(discountCode || '').trim();
            if (code) {
                url =
                    '/discount/' +
                    encodeURIComponent(code) +
                    '?redirect=' +
                    encodeURIComponent(url);
            }
            window.location.assign(url);
        },

        /**
         * Adds a line and redirects to checkout without opening the cart drawer.
         * Used by the advertorial CTA checkout A/B arm. Skips the cart clear
         * when the local cart is already empty. Waits for gift post-processors
         * only when this is a subscription and a GWP rule would add gifts.
         * Does not fire ctr:cart:added.
         * @param {Object} payload
         * @param {number|string} payload.id - Variant id.
         * @param {number} [payload.quantity=1]
         * @param {number|string} [payload.selling_plan]
         * @param {number|string} [payload.product_id] - For subscription qty cap.
         * @param {string} [payload.discountCode]
         * @returns {Promise<void>}
         */
        async addAndCheckout({
            id,
            quantity = 1,
            selling_plan,
            product_id,
            discountCode,
        } = {}) {
            const hasSellingPlan =
                selling_plan !== undefined && selling_plan !== null && selling_plan !== '';
            const normalizedQty = Number.isFinite(Number(quantity))
                ? Math.max(0, Math.floor(Number(quantity)))
                : 1;

            const body = { id, quantity: normalizedQty };
            if (hasSellingPlan) {
                body.selling_plan = selling_plan;
            }

            const waitForGifts = hasSellingPlan && this.subscriptionGiftRuleMatches(product_id);

            // Clear before beginOp so we are not fighting a display snapshot.
            // Do not wrap in withTimeout — that helper swallows errors.
            if (this.items.length) {
                await this.clearCart();
            }

            this.beginOp();

            try {
                const response = await fetch(theme.routes.cart_add_url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/javascript',
                    },
                    body: JSON.stringify(body),
                }).then((res) => res.json());

                if (response.status) {
                    const message =
                        response.description || response.message || 'Cart add failed';
                    const error = new Error(message);
                    error.shopifyResponse = response;
                    throw error;
                }

                if (!waitForGifts) {
                    this.goToCheckout(discountCode);
                    return;
                }

                this.seedCartFromAdd(response);
                await this.withTimeout(
                    this.runPostProcessors({ skipSections: true }),
                    4000
                );
                this.goToCheckout(discountCode);
            } catch (error) {
                console.error('[cart] addAndCheckout failed', error);
                throw error;
            } finally {
                this.endOp();
            }
        },

        /**
         * True when a GWP rule would add gifts for a subscription of this product.
         * Scope "all" matches every subscription. Scope "product" matches target ids.
         * @param {number|string} productId
         * @returns {boolean}
         */
        subscriptionGiftRuleMatches(productId) {
            const scripts = document.querySelectorAll('script[data-gwp-rule]');
            if (!scripts.length) return false;

            const id = Number(productId);
            for (const el of scripts) {
                let rule;
                try {
                    rule = JSON.parse(el.textContent);
                } catch (error) {
                    continue;
                }
                if (!rule || !Array.isArray(rule.variants) || !rule.variants.length) continue;
                if (rule.scope === 'product') {
                    const targets = Array.isArray(rule.targetProducts) ? rule.targetProducts : [];
                    if (targets.some((target) => Number(target) === id)) return true;
                } else {
                    return true;
                }
            }
            return false;
        },

        /**
         * Stores a /cart/add.js line as the cart contents so gift rules can
         * run without a follow-up /cart.js. Safe only after the cart was empty.
         * @param {Object} response
         * @returns {void}
         */
        seedCartFromAdd(response) {
            if (response && Array.isArray(response.items)) {
                this.data = { ...(this.data || {}), items: response.items };
                return;
            }
            if (response && response.variant_id) {
                this.data = { ...(this.data || {}), items: [response] };
            }
        },

        /**
         * Empties the cart and updates local cart state. Uses /cart/clear.js,
         * then falls back to zeroing line keys via /cart/update.js if anything
         * remains. Does not run post-processors or open the drawer.
         * @returns {Promise<void>}
         */
        async clearCart() {
            const cartBase = (theme.routes?.cart_url || '/cart').replace(/\/$/, '');
            const clearUrl = cartBase + '/clear.js';

            const res = await fetch(clearUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: '{}',
            });

            if (!res.ok) {
                throw new Error('Cart clear failed: ' + res.status);
            }

            const cart = await res.json();
            if (cart?.status) {
                const message =
                    cart.description || cart.message || 'Cart clear failed';
                const error = new Error(message);
                error.shopifyResponse = cart;
                throw error;
            }

            if (cart && Array.isArray(cart.items)) {
                this.data = cart;
            } else {
                await this.fetchCart();
            }

            const remaining = this.data?.items || [];
            if (!remaining.length) return;

            // clear.js can leave lines in some edge cases; zero them explicitly.
            const updates = {};
            remaining.forEach((item) => {
                if (item?.key) updates[item.key] = 0;
            });
            if (!Object.keys(updates).length) return;

            const updateResponse = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/json',
                },
                body: JSON.stringify({ updates }),
            }).then((r) => r.json());

            if (updateResponse?.status) {
                const message =
                    updateResponse.description ||
                    updateResponse.message ||
                    'Cart clear failed';
                const error = new Error(message);
                error.shopifyResponse = updateResponse;
                throw error;
            }

            if (updateResponse && Array.isArray(updateResponse.items)) {
                this.data = updateResponse;
            } else {
                await this.fetchCart();
            }
        },

        /**
         * Notifies listeners that the cart was updated and is ready to display.
         * Dispatched after post-processors finish and the display snapshot is
         * released so the drawer opens with the final cart state.
         * @param {Object} detail
         * @returns {void}
         */
        notifyCartAdded(detail = {}) {
            document.dispatchEvent(
                new CustomEvent('ctr:cart:added', { detail })
            );
        },

        /**
         * Destroys the cart store.
         * @returns {void}
         */
        destroy() {
            this.removeEventListeners();
        },

        /**
         * Adds event listeners to the cart store.
         * @returns {void}
         */
        addEventListeners() {
            document.addEventListener('theme:cart:add', this.onCartAdd);
        },

        /**
         * Removes event listeners from the cart store.
         * @returns {void}
         */
        removeEventListeners() {
            document.removeEventListener('theme:cart:add', this.onCartAdd);
        },

        /**
         * Cart event add product to cart
         *
         * @return  {Void}
         */
        cartAddEvent(event) {
            if (theme.settings.cartType !== 'drawer' && !this.isDirectToCheckout()) return;

            const button = event?.detail?.button;
            if (!button || button.hasAttribute('disabled')) return;

            const form = button.closest('form');
            let formData = '';
            if (!form) return;

            // Validate form
            if (!form.checkValidity()) {
                form.reportValidity();
                return;
            }

            formData = new FormData(form);
            this.syncCriticalFields(formData, form);

            // Re-resolve variant/plan from checked PDP options so a stale
            // Bag/Strawberry Lemon form value cannot win over the UI.
            const pdpForPrep = this.getCtrProductData(form);
            if (pdpForPrep && typeof pdpForPrep.prepareCartForm === 'function') {
                pdpForPrep.prepareCartForm(formData);
            }

            if (form !== null && form.querySelector('[type="file"]')) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) {
                const productId = form.dataset.ctrSubscriptionProductId;
                const requestedQty = Number(formData.get('quantity') || 1);
                const quarterly = this.isQuarterlyFormAdd(form);
                const remaining = this.getSubscriptionRemainingQty(productId, { quarterly });

                if (remaining <= 0) {
                    document.dispatchEvent(
                        new CustomEvent('ctr:subscription-qty-limit', {
                            detail: { productId, quarterly },
                        })
                    );
                    return;
                }

                if (Number.isFinite(requestedQty) && requestedQty > remaining) {
                    formData.set('quantity', String(remaining));
                }
            }

            const missingGifts = this.getMissingPurchaseGifts(form);
            if (missingGifts.length) {
                const parent = this.formDataToCartItem(formData);
                this.addToCartWithItems([parent, ...missingGifts], button);
                return;
            }

            this.addToCart(formData, button);
        },

        /**
         * Resolves Alpine ctrProduct from a form inside the product section.
         * @param {HTMLFormElement} form
         * @returns {Object|null}
         */
        getCtrProductData(form) {
            const root = form?.closest('[data-section-type="ctr-product"]');
            if (!root || typeof Alpine === 'undefined') return null;
            try {
                return Alpine.$data(root) || null;
            } catch (_) {
                return null;
            }
        },

        /**
         * True when the PDP form is adding a quarterly / 90-day subscription.
         * @param {HTMLFormElement} form
         * @returns {boolean}
         */
        isQuarterlyFormAdd(form) {
            const pdp = this.getCtrProductData(form);
            if (pdp?.purchaseMode === 'supply') return true;
            if (form?.dataset?.ctrPurchaseMode === 'supply' || form?.dataset?.ctrPurchaseMode === 'quarterly') {
                return true;
            }
            return false;
        },

        /**
         * True when a programmatic add is a quarterly subscription.
         * @param {{ properties?: Object, product_id?: number|string }} payload
         * @returns {boolean}
         */
        isQuarterlyPayload({ properties, product_id } = {}) {
            if (properties?.upgraded_in_cart_quarterly === 'true') return true;
            const id = Number(product_id);
            if (!Number.isFinite(id) || id <= 0) return false;
            return this.items.some(
                (item) => Number(item.product_id) === id && this.isQuarterlyLine(item)
            );
        },

        /**
         * Purchase-option gift lines missing from the cart for this form's PDP.
         * @param {HTMLFormElement} form
         * @returns {Array<{id: number, quantity: number, properties: Object}>}
         */
        getMissingPurchaseGifts(form) {
            const productData = this.getCtrProductData(form);
            if (!productData || typeof productData.missingPurchaseGiftItems !== 'function') {
                return [];
            }
            try {
                const gifts = productData.missingPurchaseGiftItems();
                return Array.isArray(gifts) ? gifts : [];
            } catch (_) {
                return [];
            }
        },

        /**
         * Builds a single cart/add.js item from product-form FormData.
         * @param {FormData} formData
         * @returns {{id: number, quantity: number, selling_plan?: string, properties?: Object}}
         */
        formDataToCartItem(formData) {
            const item = {
                id: Number(formData.get('id')),
                quantity: Number(formData.get('quantity') || 1) || 1,
            };

            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) {
                item.selling_plan = sellingPlan;
            }

            const properties = {};
            for (const [key, value] of formData.entries()) {
                const match = key.match(/^properties\[(.+)\]$/);
                if (match) properties[match[1]] = value;
            }
            if (Object.keys(properties).length) {
                item.properties = properties;
            }

            return item;
        },

        /**
         * Synchronizes critical form fields to avoid stale values.
         * @param {FormData} formData
         * @param {HTMLFormElement} form
         * @returns {void}
         */
        syncCriticalFields(formData, form) {
            const formId = form.getAttribute('id');

            const getInputByName = (name) => {
                const inForm = form.querySelector(`[name="${name}"]`);
                if (inForm) return inForm;
                if (!formId) return null;
                return document.querySelector(`[name="${name}"][form="${formId}"]`);
            };

            const idInput = getInputByName('id');
            if (idInput?.value) {
                formData.set('id', idInput.value);
            }

            const sellingPlanInput = getInputByName('selling_plan');
            if (sellingPlanInput) {
                if (sellingPlanInput.value) {
                    formData.set('selling_plan', sellingPlanInput.value);
                } else {
                    formData.delete('selling_plan');
                }
            }
        },

        /**
         * Direct-to-checkout mode: when armed, a successful add to cart
         * redirects straight to checkout instead of opening the cart drawer.
         * Armed by the theme setting, or externally by a script setting
         * window.__gm_dtc_variant or the gm-dtc-variant body class; the
         * gm_dtc=1 query param forces it on for QA. Unarmed = default
         * drawer behavior. B2B customers are never armed, whatever the source.
         * @returns {boolean}
         */
        isDirectToCheckout() {
            if (theme.settings?.customerB2B === true) return false;

            return (
                theme.settings?.enableDirectToCheckout === true ||
                window.__gm_dtc_variant === true ||
                document.body.classList.contains('gm-dtc-variant') ||
                window.location.search.indexOf('gm_dtc=1') > -1
            );
        },

        /**
         * Builds the checkout URL from the theme root.
         * @returns {string}
         */
        checkoutUrl() {
            const root = theme.routes?.root_url || '/';
            return (root.endsWith('/') ? root : root + '/') + 'checkout';
        },

        /**
         * Relabels the Add to Cart button(s) to "Buy Now" when
         * direct-to-checkout mode is armed. If the flag is not armed yet at
         * init, waits for the gm:dtc:armed event dispatched by the script
         * that arms it.
         * @returns {void}
         */
        initDirectToCheckoutLabel() {
            const arm = () => this.applyDirectToCheckoutLabel();

            if (this.isDirectToCheckout()) {
                arm();
                return;
            }

            document.addEventListener('gm:dtc:armed', arm, { once: true });
        },

        /**
         * Swaps the "Add to cart" label to the direct-to-checkout button
         * label on every [data-add-to-cart-text] span (product form button
         * and sticky cart bar) and re-applies it whenever the theme rewrites
         * the label on variant changes. Only the add-to-cart string is
         * replaced, so "Sold out" and "Pre order" states keep their own
         * labels.
         * @returns {void}
         */
        applyDirectToCheckoutLabel() {
            const atcLabel = (theme.strings?.addToCart || 'Add to cart').trim().toLowerCase();
            const dtcLabel = theme.settings?.directToCheckoutButtonLabel || 'Buy Now';

            const relabel = (el) => {
                if (el.textContent.trim().toLowerCase() === atcLabel) {
                    el.textContent = dtcLabel;
                }
            };

            document.querySelectorAll('[data-add-to-cart-text]').forEach((el) => {
                relabel(el);

                new MutationObserver(() => relabel(el)).observe(el, {
                    childList: true,
                    characterData: true,
                    subtree: true,
                });
            });
        },

        /**
         * Add item(s) to the cart and show the added item(s)
         *
         * @param   {String}  formData
         * @param   {DOM Element}  button
         *
         * @return  {Void}
         */
        addToCart(formData, button) {
            if (button) {
                button.classList.add('is-loading');
                button.disabled = true;
            }

            const directToCheckout = this.isDirectToCheckout();
            const isSubscription = Boolean(formData.get('selling_plan'));

            // Drawer flow needs section HTML. Subscription DTC hydrates from
            // /cart.js instead so Shopify does not render the cart drawer.
            if (!directToCheckout) {
                formData.set('sections', this.refreshSections);
            }

            let cartAddedDetail = null;
            let redirecting = false;

            this.beginOp();

            fetch(theme.routes.cart_add_url, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/javascript',
                },
                body: formData,
            })
            .then((response) => response.json())
            .then(async (response) => {
                if (response.status) {
                    console.error('Cart add failed:', response);
                    return;
                }

                // OTP DTC: leave immediately. Skipping refresh/postProcessors
                // and leaving cartAddedDetail null means ctr:cart:added never
                // fires, so the drawer never opens.
                if (directToCheckout && !isSubscription) {
                    redirecting = true;
                    this.goToCheckout();
                    return;
                }

                // DTC hydrates from the add.js lines (no /cart.js read) so the
                // gift reconcile runs at once and only fetches when a gift is
                // missing; the drawer flow hydrates from the section HTML.
                const settle = async () => {
                    await this.applyCartResponse(response, { optimistic: directToCheckout });
                    await this.runPostProcessors(
                        directToCheckout ? { skipSections: true } : {}
                    );
                };

                // Subscription DTC: wait for gifts, then redirect. Timeout so
                // a hung GWP fetch cannot trap the user on the product page.
                if (directToCheckout) {
                    await this.withTimeout(settle(), 4000);
                    redirecting = true;
                    this.goToCheckout();
                    return;
                }

                await settle();
                cartAddedDetail = { response, button };
            })
            .catch((error) => {
                console.error('Error adding item to cart:', error);
            })
            .finally(() => {
                // Keep the button in its loading state while navigating to
                // checkout so the user cannot double-submit.
                if (button && !redirecting) {
                    button.classList.remove('is-loading');
                    if (!button.hasAttribute('data-ctr-sub-qty-locked')) {
                        button.disabled = false;
                    }
                }
                this.endOp();

                if (cartAddedDetail) {
                    this.notifyCartAdded(cartAddedDetail);
                }
            });
        },

        /**
         * Adds multiple lines in one `/cart/add.js` JSON request (parent + gifts).
         * Mirrors addToCart post-response behavior (DTC, post-processors, drawer).
         * @param {Array<Object>} items - Cart line payloads for Shopify items[].
         * @param {HTMLElement} [button]
         * @returns {void}
         */
        addToCartWithItems(items, button) {
            if (button) {
                button.classList.add('is-loading');
                button.disabled = true;
            }

            const directToCheckout = this.isDirectToCheckout();
            const isSubscription = items.some((item) => Boolean(item?.selling_plan));

            const body = { items };
            if (!directToCheckout) {
                body.sections = this.refreshSections;
            }

            let cartAddedDetail = null;
            let redirecting = false;

            this.beginOp();

            fetch(theme.routes.cart_add_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/javascript',
                },
                body: JSON.stringify(body),
            })
            .then((response) => response.json())
            .then(async (response) => {
                if (response.status) {
                    console.error('Cart add failed:', response);
                    return;
                }

                if (directToCheckout && !isSubscription) {
                    redirecting = true;
                    this.goToCheckout();
                    return;
                }

                // DTC hydrates from the add.js lines (no /cart.js read) so the
                // gift reconcile runs at once and only fetches when a gift is
                // missing; the drawer flow hydrates from the section HTML.
                const settle = async () => {
                    await this.applyCartResponse(response, { optimistic: directToCheckout });
                    await this.runPostProcessors(
                        directToCheckout ? { skipSections: true } : {}
                    );
                };

                if (directToCheckout) {
                    await this.withTimeout(settle(), 4000);
                    redirecting = true;
                    this.goToCheckout();
                    return;
                }

                await settle();
                cartAddedDetail = { response, button };
            })
            .catch((error) => {
                console.error('Error adding item to cart:', error);
            })
            .finally(() => {
                if (button && !redirecting) {
                    button.classList.remove('is-loading');
                    if (!button.hasAttribute('data-ctr-sub-qty-locked')) {
                        button.disabled = false;
                    }
                }
                this.endOp();

                if (cartAddedDetail) {
                    this.notifyCartAdded(cartAddedDetail);
                }
            });
        },

        /**
         * Adds a single line to the cart programmatically.
         * @param {Object} payload
         * @param {number} payload.id - Variant id.
         * @param {number} [payload.quantity=1] - Quantity to add.
         * @param {Object} [payload.properties] - Line item properties.
         * @param {number|string} [payload.selling_plan] - Selling plan id (subscription).
         * @param {number|string} [payload.product_id] - Product id (for subscription qty cap).
         * @returns {Promise<Object>} Parsed `/cart/add.js` response.
         */
        async addItem({ id, quantity = 1, properties, selling_plan, product_id } = {}) {
            const hasSellingPlan = selling_plan !== undefined && selling_plan !== null && selling_plan !== '';
            let normalizedQty = Number.isFinite(Number(quantity)) ? Math.max(0, Math.floor(Number(quantity))) : 1;

            if (hasSellingPlan && product_id) {
                const quarterly = this.isQuarterlyPayload({ properties, product_id });
                const remaining = this.getSubscriptionRemainingQty(product_id, { quarterly });
                if (normalizedQty > remaining) {
                    const error = new Error(
                        quarterly
                            ? 'This item is limited to 1 per customer.'
                            : 'This item is limited to 3 per order.'
                    );
                    error.code = 'SUBSCRIPTION_QTY_LIMIT';
                    throw error;
                }
            }

            const body = { id, quantity: normalizedQty };
            if (properties && Object.keys(properties).length) body.properties = properties;
            if (hasSellingPlan) {
                body.selling_plan = selling_plan;
            }

            let cartAddedDetail = null;

            this.beginOp();

            try {
                const response = await fetch(theme.routes.cart_add_url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/javascript',
                    },
                    body: JSON.stringify({
                        ...body,
                        sections: this.refreshSections,
                    }),
                }).then((res) => res.json());

                if (response.status) {
                    const message = response.description || response.message || 'Cart add failed';
                    const error = new Error(message);
                    error.shopifyResponse = response;
                    throw error;
                }

                await this.applyCartResponse(response);

                await this.runPostProcessors();

                cartAddedDetail = { response };

                return response;
            } catch (error) {
                console.error('Error adding item to cart:', error);
                throw error;
            } finally {
                this.endOp();

                if (cartAddedDetail) {
                    this.notifyCartAdded(cartAddedDetail);
                }
            }
        },

        /**
         * Updates a single cart line quantity by line-item key.
         * Uses `/cart/change.js` so sibling lines that share a variant_id
         * (e.g. Free Gift discount splits) are not affected.
         * @param {string} key - The line item key to update.
         * @param {number} qty - The quantity of the item to update.
         * @returns {Promise<void>}
         */
        async updateItemQty(key, qty) {
            let normalizedQty = Number.isFinite(Number(qty)) ? Math.max(0, Math.floor(Number(qty))) : 0;

            if (normalizedQty > 0) {
                const item = this.items.find((i) => i.key === key);
                if (item && this.isSubscriptionLine(item)) {
                    const maxForLine = this.getSubscriptionMaxQtyForLine(item);
                    if (Number.isFinite(maxForLine)) {
                        normalizedQty = Math.min(normalizedQty, maxForLine);
                    }
                }
            }

            this.beginOp();

            try {
                const response = await fetch(theme.routes.cart_change_url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/javascript',
                    },
                    body: JSON.stringify({
                        id: String(key),
                        quantity: normalizedQty,
                        sections: this.refreshSections,
                    }),
                }).then((res) => res.json());

                if (response.status) {
                    const message = response.description || response.message || 'Cart change failed';
                    const error = new Error(message);
                    error.shopifyResponse = response;
                    throw error;
                }

                await this.applyCartResponse(response);

                await this.runPostProcessors();
            } catch (error) {
                console.error('Error updating item quantity:', error);
                throw error;
            } finally {
                this.endOp();
            }
        },

        /**
         * Sets the total quantity for a group of cart lines that the drawer
         * renders as a single row (same variant + selling plan, split apart by
         * Shopify's Free Gift entitlement). The whole total is placed on the
         * first key and the rest are zeroed in one `/cart/update.js` call, so
         * there is no intermediate state for the UI to flicker through.
         * Shopify is free to re-split the result; `displayItems` re-groups it.
         * @param {string[]|string} keys - Line item keys covered by the row.
         * @param {number} qty - Desired total quantity across those lines.
         * @returns {Promise<void>}
         */
        async updateGroupQty(keys, qty) {
            const keyList = (Array.isArray(keys) ? keys : [keys]).map(String).filter(Boolean);
            if (!keyList.length) return;
            if (keyList.length === 1) return this.updateItemQty(keyList[0], qty);

            let normalizedQty = Number.isFinite(Number(qty)) ? Math.max(0, Math.floor(Number(qty))) : 0;

            // Clamp against the cap at group level: everything this row covers
            // is "ours", so only lines outside the group count as already used.
            const primary = this.items.find((i) => keyList.includes(String(i.key)));
            if (normalizedQty > 0 && primary && this.isSubscriptionLine(primary)) {
                const maxForLine = this.getSubscriptionMaxQtyForLine({
                    ...primary,
                    _groupKeys: keyList,
                });
                normalizedQty = Math.min(normalizedQty, Math.max(0, maxForLine));
            }

            const updates = {};
            keyList.forEach((key, index) => {
                updates[key] = index === 0 ? normalizedQty : 0;
            });

            return this.updateLineQuantities(updates);
        },

        /**
         * Sets quantities on several cart lines in one `/cart/update.js` call,
         * so multi-line changes land atomically with no intermediate state for
         * the UI to flicker through. Delegates to `updateItemQty` for a single
         * line so the existing cap clamp still applies there.
         * @param {Object<string, number>} updates - line item key -> quantity.
         * @returns {Promise<void>}
         */
        async updateLineQuantities(updates) {
            const entries = Object.entries(updates || {}).filter(([key]) => key);
            if (!entries.length) return;
            if (entries.length === 1) return this.updateItemQty(entries[0][0], entries[0][1]);

            this.beginOp();

            try {
                const response = await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/javascript',
                    },
                    body: JSON.stringify({
                        updates: Object.fromEntries(entries),
                        sections: this.refreshSections,
                    }),
                }).then((res) => res.json());

                if (response.status) {
                    const message = response.description || response.message || 'Cart update failed';
                    const error = new Error(message);
                    error.shopifyResponse = response;
                    throw error;
                }

                await this.applyCartResponse(response);

                await this.runPostProcessors();
            } catch (error) {
                console.error('Error updating cart line quantities:', error);
                throw error;
            } finally {
                this.endOp();
            }
        },
        
    
        /**
         * Hydrates the cart from the JSON data in the #cart-data element.
         * @param {string} refreshedCartSection - The HTML of the refreshed cart section.
         * @returns {void}
         */
        hydrateFromJson(refreshedCartSection = null) {
            let cartData = document.querySelector('#cart-data');

            if (refreshedCartSection) {
                cartData = refreshedCartSection.querySelector('#cart-data');
            }
        
            if (!cartData) return;
        
            this.data = JSON.parse(cartData.textContent)
        },

        refreshCart(sectionHtml) {
            const refreshedCartSection = document.createElement('div');
            refreshedCartSection.innerHTML = sectionHtml;

            this.hydrateFromJson(refreshedCartSection);
        },

        /**
         * Hydrates the cart store from `/cart.js` (line items only, no section HTML).
         * @returns {Promise<void>}
         */
        async fetchCart() {
            try {
                const cartUrl = (theme.routes?.cart_url || '/cart').replace(/\/$/, '') + '.js';
                const data = await fetch(cartUrl, {
                    headers: { Accept: 'application/json' },
                }).then((res) => res.json());

                if (data && !data.status) {
                    this.data = data;
                }
            } catch (error) {
                console.error('[cart] fetchCart failed', error);
            }
        },

        /**
         * Applies a cart API response: section HTML when present, otherwise
         * the cart payload itself, otherwise `/cart.js`. With
         * `options.optimistic` an `/cart/add.js` response (one line, or
         * `{ items }`) is merged into the local cart instead of re-reading
         * `/cart.js`: app scripts poll `/cart.js` and Shopify serialises
         * requests per cart, so that read can take seconds. Direct to
         * checkout uses it so the redirect does not wait on that read.
         * @param {Object} [response]
         * @param {Object} [options]
         * @param {boolean} [options.optimistic] - Merge add.js lines locally.
         * @returns {Promise<void>}
         */
        async applyCartResponse(response, options = {}) {
            if (response?.sections?.[this.cartSectionId]) {
                this.refreshCart(response.sections[this.cartSectionId]);
                return;
            }
            // `/cart/update.js` returns the full cart; `/cart/add.js` does not.
            if (response && Array.isArray(response.items) && 'token' in response) {
                this.data = response;
                return;
            }
            if (options.optimistic && this.mergeAddedLines(response)) return;
            await this.fetchCart();
        },

        /**
         * Merges the lines of an `/cart/add.js` response (a line object, or
         * `{ items: [...] }`) into `data.items`: a line whose key is already
         * in the cart is replaced, new lines are appended, item_count follows.
         * The lines carry product_id, properties and selling_plan_allocation,
         * which is all the gift reconcile needs.
         * @param {Object} [response]
         * @returns {boolean} False when the response holds no lines.
         */
        mergeAddedLines(response) {
            const added = Array.isArray(response?.items)
                ? response.items
                : response && response.key
                    ? [response]
                    : [];
            const lines = added.filter((line) => line && line.key);
            if (!lines.length) return false;

            const items = [...(this.data?.items || [])];
            lines.forEach((line) => {
                const index = items.findIndex((item) => item.key === line.key);
                if (index > -1) {
                    items[index] = line;
                } else {
                    items.push(line);
                }
            });

            this.data = {
                ...(this.data || {}),
                items,
                item_count: items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
            };
            return true;
        },

        /**
         * Initializes the sections to refresh with the section rendering API.
         * @returns {void}
         */
        initSectionsToRefresh() {
            const cartData = document.querySelector('#cart-data');
            const sectionId = cartData.dataset.sectionId;
            if (!sectionId) return;

            if (this.refreshSections.includes(sectionId)) {
                return;
            }
            this.cartSectionId = sectionId;
            this.refreshSections = this.refreshSections !== '' ? `${this.refreshSections},${this.cartSectionId}` : this.cartSectionId;
        },
    

        /**
         * Returns the items in the cart.
         * @returns {Array} The items in the cart.
         */
        get items() {
            if (!this.data?.items) return [];
            // Move items with _isFreeGift: "true" in properties to the end of the list
            return [...this.data.items].sort((a, b) => {
                const aFreeGift = a.properties && a.properties._isFreeGift === "true";
                const bFreeGift = b.properties && b.properties._isFreeGift === "true";
                if (aFreeGift === bFreeGift) return 0;
                return aFreeGift ? 1 : -1;
            });
    
        },
    
        /**
         * Returns the total price of the cart.
         * @returns {number} The total price of the cart.
         */
        get totalPrice() {
            return this.data?.total_price || 0;
        },

        /**
         * Returns the total price of the cart.
         * @returns {number} The total price of the cart.
         */
        get isEmptyCart() {
            return this.data?.item_count === 0;
        },

        get totalCompareAtPrice() {
            let total = 0;

            this.items.forEach(item => {
                const totalItemPrice = (item.selling_plan_allocation?.compare_at_price || item.original_price) * item.quantity;

                total += totalItemPrice
            });

            return total;
        },

        /**
         * Returns the number of items in the cart.
         * @returns {number} The number of items in the cart.
         */
        get itemCount() {
            return this.data?.item_count || 0;
        },

        get itemCountFormatted() {
            const MAX_ITEM_COUNT = 9;
            return this.itemCount > MAX_ITEM_COUNT ? `${MAX_ITEM_COUNT}+` : this.itemCount;
        },

        /**
         * Returns the frozen snapshot during a multi-step operation, or the
         * live `data` when no operations are pending. The cart drawer UI
         * binds to `display*` getters to avoid intermediate flicker.
         * @returns {Object}
         */
        get displayData() {
            return this._opCount > 0 ? (this._dataSnapshot || {}) : this.data;
        },

        /**
         * Items list driving the cart drawer, sourced from `displayData`.
         *
         * Shopify splits a line when a discount entitles only part of its
         * quantity — the "Free Gift" BxGy entitles 1 unit, so 3 units of one
         * variant arrive as a qty-1 line plus a qty-2 line with byte-identical
         * properties. Those are collapsed here into a single display row.
         *
         * The row IS one of the real lines, with two additive underscored
         * fields (`_groupKeys`, `_groupQuantity`). Its `key`, `quantity`,
         * `properties` and prices are untouched, so `cartUpgrade`, `cartGwp`
         * and anything else reading the raw `items` list keeps working. Only
         * the drawer row itself opts into the grouped quantity.
         *
         * Grouping is by variant + selling plan + the full property set, and
         * never includes free-gift lines. Properties matter because the theme
         * also writes customer-entered ones (gift card "Recipient name",
         * preorder markers, merchant-configured custom fields) — two lines that
         * differ there are genuinely different merchandise and must stay apart.
         * @returns {Array}
         */
        get displayItems() {
            const items = this.displayData?.items;
            if (!items) return [];

            const isFreeGift = (item) => item?.properties?._isFreeGift === 'true';

            // Properties are part of the identity. Lines split by discount
            // entitlement carry byte-identical properties, so they still group;
            // lines that genuinely differ never do. This is what keeps two gift
            // cards with different "Recipient name" values — or a preorder line
            // beside a normal one — from collapsing into a single row.
            const propertiesId = (item) => {
                const props = item?.properties;
                if (!props) return '';
                const pairs = Array.isArray(props)
                    ? props.filter(Boolean).map((p) => [String(p.key ?? p.name ?? ''), String(p.value ?? '')])
                    : Object.keys(props).map((k) => [k, String(props[k] ?? '')]);
                pairs.sort((a, b) => a[0].localeCompare(b[0]));
                return JSON.stringify(pairs);
            };

            const groupIdFor = (item) =>
                `${item.variant_id}::${item.selling_plan_allocation?.selling_plan?.id ?? ''}::${propertiesId(item)}`;

            // Preserve cart order: a group takes the slot of its first member.
            const groups = new Map();
            const slots = [];

            items.forEach((item) => {
                if (isFreeGift(item)) {
                    slots.push(item);
                    return;
                }
                const groupId = groupIdFor(item);
                if (!groups.has(groupId)) {
                    const members = [];
                    groups.set(groupId, members);
                    slots.push(members);
                }
                groups.get(groupId).push(item);
            });

            const rows = slots.map((slot) => {
                if (!Array.isArray(slot)) return slot;
                // Sort by key so the row's identity stays stable regardless of
                // the order Shopify returns lines in.
                const members = [...slot].sort((a, b) => String(a.key).localeCompare(String(b.key)));
                if (members.length === 1) return members[0];

                // The row must BE the line that other components expect to find:
                // `cartUpgrade` decides which line renders the upgrade card by
                // looking for the "Free Gift" allocation in the raw items list
                // and matching on key. Render any other member and that card
                // disappears. Everything below is additive and underscored —
                // key, quantity, properties and prices stay the real line's, so
                // nothing outside the drawer row sees a synthetic object.
                const entitled = members.find((m) =>
                    (m.discounts || []).some((d) => d?.title === 'Free Gift')
                );

                return {
                    ...(entitled || members[0]),
                    _groupKeys: members.map((m) => m.key),
                    _groupQuantity: members.reduce((total, m) => total + (Number(m.quantity) || 0), 0),
                };
            });

            return rows.sort((a, b) => {
                const aFreeGift = isFreeGift(a);
                const bFreeGift = isFreeGift(b);
                if (aFreeGift === bFreeGift) return 0;
                return aFreeGift ? 1 : -1;
            });
        },

        /**
         * Subtotal (cents) for the displayed cart state.
         * @returns {number}
         */
        get displayTotalPrice() {
            return this.displayData?.total_price || 0;
        },

        /**
         * Compare-at total (cents) for the displayed cart state.
         * @returns {number}
         */
        get displayTotalCompareAtPrice() {
            let total = 0;

            this.displayItems.forEach(item => {
                // Grouped rows stand in for several lines, so count the whole
                // group here or the compare-at total silently undercounts.
                const quantity = item._groupQuantity ?? item.quantity;
                const totalItemPrice = (item.selling_plan_allocation?.compare_at_price || item.original_price) * quantity;

                total += totalItemPrice;
            });

            return total;
        },

        /**
         * Item count for the displayed cart state.
         * @returns {number}
         */
        get displayItemCount() {
            return this.displayData?.item_count || 0;
        },

        /**
         * Formatted item count badge for the displayed cart state.
         * @returns {string|number}
         */
        get displayItemCountFormatted() {
            const MAX_ITEM_COUNT = 9;
            return this.displayItemCount > MAX_ITEM_COUNT ? `${MAX_ITEM_COUNT}+` : this.displayItemCount;
        },

        /**
         * Empty-cart flag for the displayed cart state.
         * @returns {boolean}
         */
        get displayIsEmptyCart() {
            return this.displayData?.item_count === 0;
        },
    });
});